/**
 * Admin "Edit listing" full editor — exposes every public field a provider
 * could edit, PLUS the admin-only commercial / SEO / management controls.
 * Used when Strategic Tracking is building a done-for-you listing on behalf
 * of a paying provider.
 */
import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save, Eye, EyeOff, Plus, X as XIcon, ExternalLink, History } from "lucide-react";
import api, { formatApiError } from "@/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import {
  PROVIDER_TYPES, SOLUTION_CATEGORIES, DEVICE_TYPES, CONNECTIVITY,
  SENSORS, BATTERY_LIFE, COMMERCIAL_MODELS, COMPLIANCE, INDUSTRIES, USE_CASES,
} from "@/lib/taxonomies";

const PUBLISH_OPTIONS = ["Draft", "Published", "Hidden", "Pending provider review", "Pending admin review"];
const MGMT_OPTIONS = [
  { v: "admin_managed", label: "Admin managed" },
  { v: "provider_managed", label: "Provider managed" },
  { v: "admin_assisted", label: "Admin assisted" },
  { v: "unclaimed_admin_managed", label: "Unclaimed, admin managed" },
];

function MultiSelect({ label, value = [], options, onChange, testId }) {
  const [open, setOpen] = useState(false);
  const filtered = options.filter((o) => !value.includes(o));
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest text-slate-500 mb-1.5 block">{label}</Label>
      <div className="flex flex-wrap gap-1.5 p-2 border border-slate-200 rounded-md min-h-[40px] bg-white" data-testid={testId}>
        {value.map((v) => (
          <span key={v} className="tag tag-tech inline-flex items-center gap-1">
            {v}
            <button type="button" onClick={() => onChange(value.filter((x) => x !== v))} className="text-slate-500 hover:text-red-600">
              <XIcon size={11}/>
            </button>
          </span>
        ))}
        <button type="button" onClick={() => setOpen(!open)} className="text-xs text-[#002FA7] font-semibold inline-flex items-center gap-1">
          <Plus size={12}/> Add
        </button>
      </div>
      {open && (
        <div className="mt-1 border border-slate-200 rounded-md bg-white max-h-60 overflow-y-auto thin-scroll">
          {filtered.length === 0 && <div className="p-3 text-xs text-slate-400">All options selected.</div>}
          {filtered.map((o) => (
            <button
              key={o}
              type="button"
              onClick={() => { onChange([...value, o]); }}
              className="block w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50"
            >
              {o}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function GalleryEditor({ images = [], maxImages, onChange }) {
  const [url, setUrl] = useState("");
  const add = () => { if (url) { onChange([...(images || []), url]); setUrl(""); } };
  return (
    <div>
      <div className="flex gap-2 mb-3">
        <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://image-url.jpg"/>
        <button type="button" onClick={add} className="btn-secondary"><Plus size={14}/> Add</button>
      </div>
      {(images || []).length === 0 && <div className="text-xs text-slate-400">No images yet.</div>}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        {(images || []).slice(0, maxImages || 99).map((src, i) => (
          <div key={i} className="relative group">
            <img src={src} alt="" className="rounded-md border border-slate-200 aspect-video object-cover w-full"/>
            <button type="button" onClick={() => onChange(images.filter((_, j) => j !== i))}
              className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100">
              <XIcon size={12}/>
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminProviderEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [audit, setAudit] = useState([]);

  useEffect(() => {
    api.get(`/providers/${id}`).then(({ data }) => { setP(data); setDraft(data); });
    api.get(`/admin/audit-log?provider_id=${id}&limit=20`).then(({ data }) => setAudit(data.items || []));
  }, [id]);

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const save = async (publishStatus) => {
    setSaving(true);
    try {
      const payload = { ...draft };
      if (publishStatus) payload.published = publishStatus;
      // Drop unchanged top-level scalars to keep payload small.
      const { data } = await api.patch(`/providers/${id}`, payload);
      setP(data); setDraft(data);
      const { data: a } = await api.get(`/admin/audit-log?provider_id=${id}&limit=20`);
      setAudit(a.items || []);
      toast.success(publishStatus ? `Saved and set to "${publishStatus}"` : "Saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (!draft) return <div className="max-w-5xl mx-auto px-6 py-20 text-slate-500">Loading…</div>;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <button onClick={() => navigate("/admin")} className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 mb-4" data-testid="edit-back">
        <ArrowLeft size={14}/> Back to admin
      </button>
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Listing editor</div>
          <h1 className="font-display font-extrabold text-3xl text-slate-900 mt-1" data-testid="edit-name">{draft.name}</h1>
          <div className="text-xs text-slate-500 mt-1 font-mono">/{draft.slug} · {draft.management_mode || "—"}</div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link to={`/solutions/${draft.slug}`} target="_blank" className="btn-secondary" data-testid="edit-preview">
            <Eye size={14}/> Preview public page <ExternalLink size={12}/>
          </Link>
          <button onClick={() => save("Draft")} disabled={saving} className="btn-secondary" data-testid="edit-save-draft">
            <Save size={14}/> Save draft
          </button>
          <button onClick={() => save("Published")} disabled={saving} className="btn-primary" data-testid="edit-publish">
            <Save size={14}/> {saving ? "Saving…" : "Save & publish"}
          </button>
          {draft.published === "Published" && (
            <button onClick={() => save("Hidden")} disabled={saving} className="btn-secondary text-red-600" data-testid="edit-hide">
              <EyeOff size={14}/> Hide
            </button>
          )}
        </div>
      </div>

      <Tabs defaultValue="content">
        <TabsList className="bg-slate-100 mb-6" data-testid="edit-tabs">
          <TabsTrigger value="content" data-testid="tab-edit-content">Content</TabsTrigger>
          <TabsTrigger value="taxonomy" data-testid="tab-edit-taxonomy">Taxonomy</TabsTrigger>
          <TabsTrigger value="media" data-testid="tab-edit-media">Media</TabsTrigger>
          <TabsTrigger value="commercial" data-testid="tab-edit-commercial">Commercial & Toggles</TabsTrigger>
          <TabsTrigger value="seo" data-testid="tab-edit-seo">SEO</TabsTrigger>
          <TabsTrigger value="ownership" data-testid="tab-edit-ownership">Ownership</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-edit-audit">Audit log</TabsTrigger>
        </TabsList>

        <TabsContent value="content" className="space-y-5">
          <div className="card-elevated p-6 space-y-5">
            <Field label="Company name *"><Input value={draft.name || ""} onChange={(e) => set("name", e.target.value)} data-testid="edit-input-name"/></Field>
            <Field label="Tagline (one-line)"><Input value={draft.tagline || ""} maxLength={200} onChange={(e) => set("tagline", e.target.value)}/></Field>
            <Field label="Summary (2-3 sentences)"><Textarea rows={3} value={draft.summary || ""} onChange={(e) => set("summary", e.target.value)}/></Field>
            <Field label="Full description"><Textarea rows={8} value={draft.description || ""} onChange={(e) => set("description", e.target.value)}/></Field>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Website"><Input value={draft.website || ""} onChange={(e) => set("website", e.target.value)}/></Field>
              <Field label="Contact email"><Input value={draft.contact_email || ""} onChange={(e) => set("contact_email", e.target.value)}/></Field>
              <Field label="Phone"><Input value={draft.contact_phone || ""} onChange={(e) => set("contact_phone", e.target.value)}/></Field>
              <Field label="Main contact person"><Input value={draft.main_contact || ""} onChange={(e) => set("main_contact", e.target.value)}/></Field>
              <Field label="HQ city"><Input value={draft.hq_city || ""} onChange={(e) => set("hq_city", e.target.value)}/></Field>
              <Field label="HQ country"><Input value={draft.hq_country || ""} onChange={(e) => set("hq_country", e.target.value)}/></Field>
              <Field label="Founded year"><Input type="number" value={draft.founded_year || ""} onChange={(e) => set("founded_year", e.target.value ? Number(e.target.value) : null)}/></Field>
              <Field label="Company size"><Input value={draft.company_size || ""} onChange={(e) => set("company_size", e.target.value)}/></Field>
              <Field label="LinkedIn"><Input value={draft.linkedin_url || ""} onChange={(e) => set("linkedin_url", e.target.value)}/></Field>
              <Field label="YouTube"><Input value={draft.youtube_url || ""} onChange={(e) => set("youtube_url", e.target.value)}/></Field>
              <Field label="Twitter / X"><Input value={draft.twitter_url || ""} onChange={(e) => set("twitter_url", e.target.value)}/></Field>
              <Field label="Facebook"><Input value={draft.facebook_url || ""} onChange={(e) => set("facebook_url", e.target.value)}/></Field>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="taxonomy" className="space-y-5">
          <div className="card-elevated p-6 space-y-5">
            <MultiSelect label="Provider types" value={draft.provider_types || []} options={PROVIDER_TYPES} onChange={(v) => set("provider_types", v)}/>
            <MultiSelect label="Solution categories" value={draft.solution_categories || []} options={SOLUTION_CATEGORIES} onChange={(v) => set("solution_categories", v)}/>
            <MultiSelect label="Industries served" value={draft.industries || []} options={INDUSTRIES} onChange={(v) => set("industries", v)}/>
            <MultiSelect label="Device / product types" value={draft.device_types || []} options={DEVICE_TYPES} onChange={(v) => set("device_types", v)}/>
            <MultiSelect label="Connectivity" value={draft.connectivity || []} options={CONNECTIVITY} onChange={(v) => set("connectivity", v)}/>
            <MultiSelect label="Sensors" value={draft.sensors || []} options={SENSORS} onChange={(v) => set("sensors", v)}/>
            <MultiSelect label="Battery / power" value={draft.battery_life || []} options={BATTERY_LIFE} onChange={(v) => set("battery_life", v)}/>
            <MultiSelect label="Commercial models" value={draft.commercial_models || []} options={COMMERCIAL_MODELS} onChange={(v) => set("commercial_models", v)}/>
            <MultiSelect label="Compliance" value={draft.compliance || []} options={COMPLIANCE} onChange={(v) => set("compliance", v)}/>
            <MultiSelect label="Use cases" value={draft.use_cases || []} options={USE_CASES} onChange={(v) => set("use_cases", v)}/>
          </div>
        </TabsContent>

        <TabsContent value="media" className="space-y-5">
          <div className="card-elevated p-6 space-y-5">
            <Field label="Logo URL"><Input value={draft.logo_url || ""} onChange={(e) => set("logo_url", e.target.value)}/></Field>
            <Field label="Cover image URL"><Input value={draft.cover_image_url || ""} onChange={(e) => set("cover_image_url", e.target.value)}/></Field>
            <Field label="Video URL"><Input value={draft.video_url || ""} onChange={(e) => set("video_url", e.target.value)}/></Field>
            <Field label="Case study URL"><Input value={draft.case_study_url || ""} onChange={(e) => set("case_study_url", e.target.value)}/></Field>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500 mb-1.5 block">Gallery images</Label>
              <GalleryEditor images={draft.images} maxImages={draft.max_gallery_images || 8} onChange={(v) => set("images", v)}/>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="commercial" className="space-y-5">
          <div className="card-elevated p-6 grid md:grid-cols-3 gap-6">
            <div>
              <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Public toggles</div>
              <ToggleRow label="Show Verified badge" checked={!!draft.show_verified_badge} onChange={(v) => set("show_verified_badge", v)} testId="edit-toggle-verified"/>
              <ToggleRow label="Show Featured badge" checked={!!draft.show_featured_badge} onChange={(v) => set("show_featured_badge", v)} testId="edit-toggle-featured"/>
              <ToggleRow label="Homepage featured" checked={!!draft.homepage_featured} onChange={(v) => set("homepage_featured", v)}/>
              <ToggleRow label="Quote button" checked={!!draft.enable_quote_button} onChange={(v) => set("enable_quote_button", v)} testId="edit-toggle-quote"/>
              <ToggleRow label="Direct lead routing" checked={!!draft.enable_direct_leads} onChange={(v) => set("enable_direct_leads", v)}/>
              <ToggleRow label="Show contact details" checked={!!draft.show_contact_details} onChange={(v) => set("show_contact_details", v)}/>
              <Field label="Priority weight"><Input type="number" value={draft.priority_search_weight || 0} onChange={(e) => set("priority_search_weight", Number(e.target.value))}/></Field>
              <Field label="Tier">
                <Select value={draft.tier} onValueChange={(v) => set("tier", v)}>
                  <SelectTrigger data-testid="edit-tier"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    {["Basic","Verified","Featured"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Lead routing">
                <Select value={draft.lead_routing || "admin_only"} onValueChange={(v) => set("lead_routing", v)}>
                  <SelectTrigger><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin_only">Admin only</SelectItem>
                    <SelectItem value="provider_only">Provider only</SelectItem>
                    <SelectItem value="both">Both</SelectItem>
                    <SelectItem value="logged">Logged only</SelectItem>
                    <SelectItem value="disabled">Disabled</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Media access</div>
              <ToggleRow label="Enable gallery" checked={!!draft.enable_gallery} onChange={(v) => set("enable_gallery", v)}/>
              <ToggleRow label="Enable video" checked={!!draft.enable_video} onChange={(v) => set("enable_video", v)}/>
              <ToggleRow label="Enable case studies" checked={!!draft.enable_case_studies} onChange={(v) => set("enable_case_studies", v)}/>
              <ToggleRow label="Lead dashboard for provider" checked={!!draft.enable_lead_dashboard} onChange={(v) => set("enable_lead_dashboard", v)}/>
              <Field label="Max gallery images"><Input type="number" value={draft.max_gallery_images || 0} onChange={(e) => set("max_gallery_images", Number(e.target.value))}/></Field>
              <Field label="Verified (admin set)">
                <ToggleRow label="" checked={!!draft.verified} onChange={(v) => set("verified", v)}/>
              </Field>
              {draft.verified_at && <div className="text-xs text-slate-500 mt-2">Verified at {new Date(draft.verified_at).toLocaleString()} by {draft.verified_by}</div>}
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Commercial (internal)</div>
              <Field label="Status">
                <Select value={draft.commercial_status || "Free"} onValueChange={(v) => set("commercial_status", v)}>
                  <SelectTrigger><SelectValue/></SelectTrigger>
                  <SelectContent>{["Free","Trial","Paid","Comped","Partner","Suspended","Churned"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Agreed price"><Input value={draft.agreed_price || ""} onChange={(e) => set("agreed_price", e.target.value)} placeholder="$2,400/yr"/></Field>
              <Field label="Billing frequency">
                <Select value={draft.billing_frequency || "none"} onValueChange={(v) => set("billing_frequency", v === "none" ? "" : v)}>
                  <SelectTrigger><SelectValue placeholder="—"/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">—</SelectItem>
                    {["Monthly","Annual","One-off","Manual"].map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Invoice status">
                <Select value={draft.invoice_status || "Not invoiced"} onValueChange={(v) => set("invoice_status", v)}>
                  <SelectTrigger><SelectValue/></SelectTrigger>
                  <SelectContent>{["Not invoiced","Invoice sent","Paid","Overdue","Cancelled"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Renewal date"><Input type="date" value={draft.renewal_date || ""} onChange={(e) => set("renewal_date", e.target.value)}/></Field>
              <Field label="Relationship owner"><Input value={draft.relationship_owner || ""} onChange={(e) => set("relationship_owner", e.target.value)}/></Field>
              <Field label="Admin commercial notes"><Textarea rows={3} value={draft.admin_commercial_notes || ""} onChange={(e) => set("admin_commercial_notes", e.target.value)}/></Field>
            </div>
          </div>

          <StripeBillingPanel
            providerId={id}
            billing={draft.billing}
            onSaved={(updated) => setDraft((d) => ({ ...d, billing: updated.billing }))}
          />
        </TabsContent>

        <TabsContent value="seo" className="space-y-5">
          <div className="card-elevated p-6 space-y-5">
            <Field label="SEO title override (max ~60 chars)"><Input value={draft.seo_title || ""} maxLength={70} onChange={(e) => set("seo_title", e.target.value)} placeholder={`${draft.name} | Strategic Tracking`}/></Field>
            <Field label="SEO meta description (max ~160 chars)"><Textarea rows={2} value={draft.seo_description || ""} maxLength={170} onChange={(e) => set("seo_description", e.target.value)} placeholder={draft.tagline || draft.summary}/></Field>
            <Field label="Open Graph image URL override"><Input value={draft.og_image_override || ""} onChange={(e) => set("og_image_override", e.target.value)} placeholder="Leave blank to use auto-generated /api/og/provider/{slug}.png"/></Field>
            <Field label="Canonical URL override"><Input value={draft.canonical_url || ""} onChange={(e) => set("canonical_url", e.target.value)} placeholder="Optional"/></Field>
            <ToggleRow label="No-index (hide from search engines)" checked={!!draft.noindex} onChange={(v) => set("noindex", v)} testId="edit-toggle-noindex"/>
            <Field label="Slug (read-only)"><Input value={draft.slug || ""} disabled className="font-mono"/></Field>
          </div>
        </TabsContent>

        <TabsContent value="ownership" className="space-y-5">
          <div className="card-elevated p-6 space-y-5">
            <Field label="Publish status">
              <Select value={draft.published || "Draft"} onValueChange={(v) => set("published", v)}>
                <SelectTrigger data-testid="edit-published"><SelectValue/></SelectTrigger>
                <SelectContent>{PUBLISH_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Claim status">
              <Select value={draft.claim_status || "Unclaimed"} onValueChange={(v) => set("claim_status", v)}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent>{["Unclaimed","Pending","Claimed"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Management mode">
              <Select value={draft.management_mode || "unclaimed_admin_managed"} onValueChange={(v) => set("management_mode", v)}>
                <SelectTrigger data-testid="edit-mgmt-mode"><SelectValue/></SelectTrigger>
                <SelectContent>{MGMT_OPTIONS.map((o) => <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Owner user ID (assign provider user)"><Input value={draft.owner_user_id || ""} onChange={(e) => set("owner_user_id", e.target.value)} placeholder="UUID of the provider's account"/></Field>
            <Field label="Admin notes (internal)"><Textarea rows={3} value={draft.admin_notes || ""} onChange={(e) => set("admin_notes", e.target.value)}/></Field>
          </div>
        </TabsContent>

        <TabsContent value="audit">
          <div className="card-elevated p-6">
            <div className="flex items-center gap-2 mb-4 text-slate-700">
              <History size={16}/>
              <div className="font-display font-semibold">Recent changes</div>
            </div>
            {audit.length === 0 ? (
              <div className="text-sm text-slate-500">No changes logged yet.</div>
            ) : (
              <ul className="divide-y divide-slate-100 text-sm">
                {audit.map((e) => (
                  <li key={e.id} className="py-2 grid grid-cols-[140px_1fr] gap-3">
                    <div className="text-xs text-slate-500">{new Date(e.created_at).toLocaleString()}</div>
                    <div>
                      <span className="font-semibold text-slate-900">{e.field}</span>
                      <span className="text-xs text-slate-500 ml-2">{e.actor_role || "?"} · {e.admin_email || "system"}</span>
                      <div className="text-xs text-slate-600 mt-0.5 font-mono">
                        {JSON.stringify(e.previous_value) ?? "—"} → {JSON.stringify(e.new_value) ?? "—"}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest text-slate-500 mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}

function ToggleRow({ label, checked, onChange, testId }) {
  return (
    <label className="flex items-center justify-between text-sm py-1.5 border-b border-slate-100">
      <span className="text-slate-700">{label}</span>
      <Switch checked={!!checked} onCheckedChange={onChange} data-testid={testId}/>
    </label>
  );
}


function StripeBillingPanel({ providerId, billing, onSaved }) {
  const b = billing || {};
  const has = Object.keys(b).length > 0;

  const APP_STATUSES = [
    "No subscription", "Trial requested", "Trial active", "Active paid",
    "Payment failed", "Past due", "Cancelled", "Manually comped", "Manual invoice",
  ];
  const ACTIVATION = ["Pending review", "Approved", "Hold", "Rejected"];

  const statusStyles = {
    "Active paid": "bg-emerald-100 text-emerald-800 border-emerald-200",
    "Trial active": "bg-amber-100 text-amber-800 border-amber-200",
    "Trial requested": "bg-amber-50 text-amber-700 border-amber-200",
    "Payment failed": "bg-red-100 text-red-800 border-red-200",
    "Past due": "bg-red-50 text-red-700 border-red-200",
    "Cancelled": "bg-slate-200 text-slate-700 border-slate-300",
    "Manually comped": "bg-indigo-100 text-indigo-800 border-indigo-200",
    "Manual invoice": "bg-indigo-50 text-indigo-700 border-indigo-200",
    "No subscription": "bg-slate-100 text-slate-600 border-slate-200",
  };
  const activationStyles = {
    "Approved": "bg-emerald-100 text-emerald-800 border-emerald-200",
    "Pending review": "bg-amber-100 text-amber-800 border-amber-200",
    "Hold": "bg-slate-200 text-slate-700 border-slate-300",
    "Rejected": "bg-red-100 text-red-800 border-red-200",
  };

  const [billingStatus, setBillingStatus] = useState(b.billing_status || "");
  const [activation, setActivation] = useState(b.admin_activation_status || "Pending review");
  const [notes, setNotes] = useState(b.admin_activation_notes || "");
  const [saving, setSaving] = useState(false);

  // re-sync if the parent draft refreshes
  useEffect(() => {
    setBillingStatus(b.billing_status || "");
    setActivation(b.admin_activation_status || "Pending review");
    setNotes(b.admin_activation_notes || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [b.billing_status, b.admin_activation_status, b.admin_activation_notes]);

  const fmt = (s) => (s ? new Date(s).toLocaleString() : "—");
  const dirty = (
    (billingStatus || "") !== (b.billing_status || "") ||
    (activation || "Pending review") !== (b.admin_activation_status || "Pending review") ||
    (notes || "") !== (b.admin_activation_notes || "")
  );

  const save = async () => {
    setSaving(true);
    try {
      const body = {};
      if (billingStatus) body.billing_status = billingStatus;
      if (activation) body.admin_activation_status = activation;
      body.admin_activation_notes = notes || "";
      const { data } = await api.patch(`/admin/providers/${providerId}/billing`, body);
      toast.success("Billing record updated.");
      onSaved?.(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card-elevated p-6 border-2 border-dashed border-slate-200" data-testid="edit-stripe-billing">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Stripe billing</div>
          <div className="font-display font-bold text-slate-900 mt-1">Billing state is separate from public listing tier</div>
          <p className="text-xs text-slate-500 mt-1 max-w-2xl leading-relaxed">
            Automatic fields are populated by the Stripe webhook. A provider may have an active Stripe subscription while
            their public listing tier (Basic / Verified / Featured) stays unchanged — toggle the tier and benefit switches
            above only when you are ready to activate publicly.
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          {b.billing_status && (
            <span className={`tag border ${statusStyles[b.billing_status] || "bg-slate-100 text-slate-700"}`} data-testid="edit-billing-status-chip">
              {b.billing_status}
            </span>
          )}
          {b.admin_activation_status && (
            <span className={`tag border ${activationStyles[b.admin_activation_status] || "bg-slate-100 text-slate-700"}`} data-testid="edit-activation-status-chip">
              {b.admin_activation_status}
            </span>
          )}
        </div>
      </div>

      {!has && (
        <div className="mt-4 mb-4 text-sm text-slate-400 italic">No Stripe events received for this provider yet.</div>
      )}

      <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3 text-sm">
        <BillingItem label="Stripe customer ID" value={b.stripe_customer_id} mono/>
        <BillingItem label="Stripe subscription ID" value={b.stripe_subscription_id} mono/>
        <BillingItem label="Stripe product ID" value={b.stripe_product_id} mono/>
        <BillingItem label="Stripe price ID" value={b.stripe_price_id} mono/>
        <BillingItem label="Billing plan requested" value={b.billing_plan_requested}/>
        <BillingItem label="Stripe payment link used" value={b.stripe_payment_link_used} mono/>
        <BillingItem label="Email on file" value={b.email}/>
        <BillingItem label="Trial start" value={fmt(b.trial_start_date)}/>
        <BillingItem label="Trial end" value={fmt(b.trial_end_date)}/>
        <BillingItem label="Current period start" value={fmt(b.current_period_start)}/>
        <BillingItem label="Current period end" value={fmt(b.current_period_end)}/>
        <BillingItem label="Last payment status" value={b.last_payment_status}/>
        <BillingItem label="Cancel at period end" value={b.cancel_at_period_end == null ? null : (b.cancel_at_period_end ? "Yes" : "No")}/>
        <BillingItem label="Canceled at" value={fmt(b.canceled_at)}/>
        <BillingItem label="Raw Stripe status" value={b.stripe_status} mono/>
        <BillingItem label="Last invoice amount" value={b.last_invoice_amount_paid != null ? `$${(b.last_invoice_amount_paid / 100).toFixed(2)}` : null}/>
        <BillingItem label="Last invoice event" value={b.last_invoice_event}/>
        <BillingItem label="Last event at" value={fmt(b.last_event_at)}/>
      </div>

      <div className="mt-6 border-t border-slate-200 pt-5">
        <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">Admin manual overrides</div>
        <div className="grid md:grid-cols-3 gap-4">
          <Field label="Billing status (app-level)">
            <Select value={billingStatus || undefined} onValueChange={setBillingStatus}>
              <SelectTrigger data-testid="edit-billing-status"><SelectValue placeholder="Auto from Stripe"/></SelectTrigger>
              <SelectContent>{APP_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Admin activation status">
            <Select value={activation || "Pending review"} onValueChange={setActivation}>
              <SelectTrigger data-testid="edit-activation-status"><SelectValue/></SelectTrigger>
              <SelectContent>{ACTIVATION.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Activation notes">
            <Textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Why is this still on hold? What needs reviewing?"
              data-testid="edit-activation-notes"
            />
          </Field>
        </div>
        <div className="mt-4 flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={async () => {
              try {
                await api.post(`/admin/brevo-sync/resync/${providerId}`);
                toast.success("Brevo resync queued.");
              } catch (e) { toast.error(formatApiError(e)); }
            }}
            className="text-xs text-slate-500 hover:text-slate-900 underline"
            data-testid="edit-brevo-resync"
          >
            Resync this contact to Brevo (list 34)
          </button>
          <button
            type="button"
            disabled={!dirty || saving}
            onClick={save}
            className="text-sm font-semibold inline-flex items-center gap-2 rounded-md h-10 px-4 bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="edit-billing-save"
          >
            {saving ? "Saving…" : "Save billing overrides"}
          </button>
        </div>
      </div>
    </div>
  );
}

function BillingItem({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`text-slate-900 ${mono ? "font-mono text-xs break-all" : ""}`}>{value || <span className="text-slate-300">—</span>}</div>
    </div>
  );
}
